import React, { useState, useEffect } from 'react';
import Util from 'components/layout_and_quote_builder/quote_builder/Util.jsx';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import MyEventListener from 'components/common/MyEventListener.jsx'
import Constants from 'components/layout_and_quote_builder/Constants.jsx'
import LayoutSvgDownload from 'components/layout_and_quote_builder/quote_builder/LayoutSvgDownload.jsx'
import FrankSolutionDimensions from 'components/frank/FrankSolutionDimensions.jsx'
import PostRequest from 'components/common/PostRequest.jsx';
import resetSelectedListHandler from 'components/layout_and_quote_builder/quote_builder/resetSelectedListHandler.js';
import { getFromCache, hasInCache, subscribeToCache } from 'components/layout_and_quote_builder/utils/SolutionScoreCache.js';
import frankStyles from 'components/frank/_style.module.scss';

// Frank's priced-solution SVG table -- the two-column (printing | die cutting) picture, dimensions and
// downloads shown once a solution is selected on /mcc. A FORK of
// components/layout_and_quote_builder/quote_builder/LayoutSvg.jsx (its origin; diff against it to review this),
// taken so Frank can diverge without touching the frozen legacy page, which keeps rendering LayoutSvg. This copy
// is Frank-only, so every `window.frank_mode` branch the original carried is collapsed to its Frank value
// (printing first, the portrait quarter-turn, machine names from the score cache, the fixed 50% columns).
//
// FIRST DIVERGENCE -- a consistent on-screen line weight regardless of dieline size. The injected SVG's strokes
// are baked in geometry units (stroke_width: 2 -> SvgWriter) and scale with svg-pan-zoom's fit, so a huge
// dieline renders sub-pixel hairlines. frankStyles.blackBgSvg gives them `vector-effect: non-scaling-stroke`, so
// they render at a fixed screen weight whatever the dieline's size. The DOWNLOADS are untouched -- they carry
// the real geometry.
const FrankSolutionSvgTable = ( { index, item, options, presentedOptions, navSection, selectedList, setSelectedList, multipleColumns} ) => {

  const _multipleColumns = true;
  const getSolution = () => {
    if(item == undefined)         { return }

    if(selectedList == undefined) { return }

    // First, try to find an exact match by item UUID
    for(let i=0; i < selectedList.length; i++) {
      if(selectedList[i].diecutting_result.member.uuid == item.uuid) {
        return selectedList[i]
      }
    }

    // If we have a selectedList but couldn't find a matching solution,
    // something went wrong - track this as an error
    if(selectedList.length > 0) {
      trackEvent('js_error_happened', {
        exception: '[FrankSolutionSvgTable#getSolution] Failed to find solution for item',
        item_uuid: item.uuid,
        selectedList_length: selectedList.length,
        selectedList_uuids: selectedList.map(s => s.diecutting_result?.member?.uuid),
        navSection_index: index,
        user_quote_id: window.user_quote_id
      });
    }
  }
  const [solution, setSolution] = useState( getSolution() )

  // THE SOLUTION'S SCORE, which is where the machine names live. Read from the shared cache rather than
  // fetched: the solution card shown above this table on /mcc is already the one that asks for it (and on the
  // click-through path the score arrives with the solve and is cached before this even mounts), so asking again
  // would be a second heavy request for an answer the page already has. Subscribing covers the deep-link path,
  // where a priced section reopens with a cold cache and the card's fetch lands after this has rendered.
  const [scoreData, setScoreData] = useState(null);

  // Drop the selection, server included. No button offers this any more -- the priced view's own "choose another
  // solution" does that -- but the section still clears itself when its LAYOUT DIMENSION changes underneath it
  // (see onListen): the solution was solved for a sheet that is no longer the sheet.
  const handleClear = () => {
    resetSelectedListHandler({ index, setSelectedList, setSolution });
  }

  const downloadBtn = (type, format) => {
    let svgParams = getSvgPostParams(type)
    let defaultFileName = [ window.catalog_name, t(`layouts.download_type_${type}`)].join('-')
    if(index > 0) { defaultFileName = defaultFileName + "-" + `${index + 1}` }
    // The Frank download route: LayoutSvgDownload is shared with the legacy LayoutSvg, which does not pass this
    // and keeps its own /layouts_nav_section_combination_download. /mcc's export goes to Frank::Export instead.
    return <LayoutSvgDownload solution={solution} type={type} svgParams={svgParams} format={format} defaultFileName={defaultFileName} multipleColumns={_multipleColumns} urlBase="/mcc/layouts-nav-section-combination-download" />
  }

  // The downloads, and ONLY the downloads. The Back button that used to sit at the end of each row is gone: the
  // priced view already carries one reset -- ManufacturingLineLists' "choose another solution" button above this
  // table -- and this was a second one beside it, twice over, once per column. Four ways to undo one choice.
  const controls = (type) => (
    <table className={frankStyles.downloadControls}>
      <tbody>
        <tr>
          <th>
            {downloadBtn(type, 'dxf')} &nbsp;  &nbsp;
            {downloadBtn(type, 'svg')} &nbsp;  &nbsp;
            {downloadBtn(type, 'pdf')}
          </th>
        </tr>
      </tbody>
    </table>
  );

  const description_for = (layout_dimension) => {
    const { unit, locale } = window.user_quote;
    return layout_dimension[`description_${locale}_${unit}`];
  };

  // The sheet the die-cutting machine runs: an l_shape die-cutting solution runs the whole printed
  // L sheet in one pass (digital cutter), so its label is the printed sheet, not the single copy.
  const dieCuttingSheetLayoutDimension = () => {
    if (solution.diecutting_result.l_shape) {
      return solution.diecutting_result.printing_result.layout_dimension;
    }
    return solution.diecutting_result.layout_dimension;
  };


  const svgHeight = 380;

  // COLUMN ORDER: printing first, then die cutting -- the order the section is actually manufactured in, and
  // the order /mcc's own cards already read in (both the solution card, LayoutNavSectionCombination, and the
  // ManufacturingLine card put printing on the left). Always, here: this is the Frank-only component (the legacy
  // LayoutSvg kept die-cutting first behind its `frank_mode` flag).

  // Is this sheet stored standing up? The SVG is drawn in its layout_dimension's own orientation, so this is
  // what decides whether a column comes out portrait -- see #columnPicture.
  const isPortrait = (layout_dimension) => (
    parseFloat(layout_dimension.height) > parseFloat(layout_dimension.width)
  );

  // EVERY SHEET LIES LANDSCAPE. Both passes run their sheet long-side-first through a real machine, but a
  // LayoutDimension can be STORED standing up (a B3 is 353 x 500), and the picture is drawn in whatever
  // orientation its dimension has -- so the two columns could show the SAME physical sheet lying two different
  // ways up. A quarter turn when the sheet is portrait settles it, which is the rule the solution card already
  // applies to its own figures (LayoutNavSectionCombination's printingRotate / dieCuttingRotate).
  //
  // The PICTURE only: the DXF / SVG / PDF downloads still carry the solution's real geometry.
  //
  // The turned box has to be SQUARE, or the turn would change the cell's shape: rotate(90deg) swaps a box's
  // width and height on screen, so only a square lands back on its own footprint. BOTH columns get that same
  // square, turned or not -- a square only for the turned one caps it while the other goes on growing with the
  // cell, so the two sheets end up drawn at different scales, which is the very thing the equal 50% columns
  // were meant to stop.
  //
  // ITS SIDE IS CAPPED BY THE COLUMN, not fixed at svgHeight. A section drawn beside another one -- lamination
  // adds a second card, and the two share the width -- leaves each column about 337px, and a fixed 380px square
  // no longer fits: as a flex item it was squashed to the column's width while KEEPING its 380px height, and
  // the quarter turn then swung that 380px back across the screen. That put 13px of black over the card's edge
  // on either side of every column, so the two cards' pictures met in the gap between them, which is the
  // overlap this reads as. min(svgHeight, 100%) on the width with a 1:1 aspect ratio keeps the box square and
  // never wider than the column holding it; a section on its own still gets the full svgHeight it always had.
  //
  // The svg fills that box (frankStyles.blackBgSvg) and svg-pan-zoom fits the sheet inside the svg, so capping
  // the box is what zooms the sheet out -- there is no second place holding a size that could disagree.
  const columnPicture = (svg, layout_dimension) => {
    if (svg === '') { return svg; }

    const turned = isPortrait(layout_dimension);

    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div
          data-rotated-to-landscape={turned ? true : undefined}
          style={{
            width: `min(${svgHeight}px, 100%)`,
            aspectRatio: '1 / 1',
            ...(turned ? { transform: 'rotate(90deg)' } : {})
          }}
        >
          {svg}
        </div>
      </div>
    );
  };

  // WHICH MACHINE RUNS THIS PASS, named under the heading. The solution card that sits above this table says
  // "KBA Rapida 76 / Offset" and "Heidelberg Cylinder SBG / Kazanlı"; the table said only "Baskı" and "Bıçak",
  // so nothing on the priced page connected the card the viewer chose to the solution they are now pricing.
  // Same two labels, from the same score response the card reads, so the two cannot drift apart.
  //
  // Absent until the score lands, and absent for a solution whose machine has no price (the score answers with
  // no label) -- the heading is then simply on its own, as it always was.
  const machineFor = (type) => {
    if (scoreData == null) { return {}; }

    return type === 'printing'
      ? { name: scoreData.printing_machine_label, typeLabel: scoreData.printing_machine_type_label }
      : { name: scoreData.die_cutting_machine_label, typeLabel: scoreData.die_cutting_machine_type_label };
  };

  // The two columns as data, so the pair can be ordered per page without writing the table out twice. Each
  // holds everything one column contributes to every row of the table.
  const solutionColumns = () => {
    const dieCutting = {
      key: 'die_cutting',
      heading: t('layouts.layout_type_die_cutting'),
      machine: machineFor('die_cutting'),
      // Turned by the SAME sheet the column is labelled with, so the label and the picture cannot disagree.
      svg: columnPicture(showDiecuttingSvg(), dieCuttingSheetLayoutDimension()),
      description: description_for(dieCuttingSheetLayoutDimension()),
      dimensions: <FrankSolutionDimensions type="diecutting" solution={solution} />,
      controls: controls('die_cutting')
    };

    const printing = {
      key: 'printing',
      heading: t('layouts.layout_type_printing'),
      machine: machineFor('printing'),
      svg: columnPicture(showPrintingSvg(), solution.diecutting_result.printing_result.layout_dimension),
      description: description_for(solution.diecutting_result.printing_result.layout_dimension),
      dimensions: <FrankSolutionDimensions type="printing" solution={solution} />,
      controls: controls('printing')
    };

    return [printing, dieCutting];
  }

  const solutionSvg = () => {
    const columns = solutionColumns();
    // The download buttons hug the table's OUTER edges: the left column's sit left (the default) and the right
    // column's are floated right. That is a property of the POSITION, not of the pass, so it follows whichever
    // column ends up on the right.
    const controlsFor = (column, position) => (
      position === columns.length - 1
        ? <div className="float-right"> {column.controls} </div>
        : column.controls
    );

    // The machine written under the pass it runs -- name above type, the way the solution card writes it.
    const machineName = (column) => {
      if (!column.machine.name) { return null; }

      return (
        // No negative pull: the heading is an <h4> and its own bottom margin is the gap. Tightening it ran the
        // machine name into the heading above -- "Baskı" and "KBA Rapida 145" read as one crowded block.
        // The greys are the SOLUTION CARD's own (LayoutNavSectionCombination#machineWithName), on purpose and
        // not the design language's --ink / --ink-faint: this block exists to read as the same thing the card
        // says, and the tokens are different values (#10141a / #838d99), so using them here would make the two
        // diverge. Migrating both together is the way to move this to tokens.
        <div data-column-machine={column.key} style={{ textAlign: 'center', paddingBottom: '4px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#222', lineHeight: 1.2 }}>
            {column.machine.name}
          </div>
          {column.machine.typeLabel ? (
            <div style={{ fontSize: '12px', fontWeight: 400, color: '#888', lineHeight: 1.2 }}>
              {column.machine.typeLabel}
            </div>
          ) : null}
        </div>
      );
    };

    // EQUAL COLUMNS. A table sizes its columns to their content by default, and the two passes never carry the
    // same amount: the die-cutting column carries an output size the printing one has no equivalent of (and, at
    // the time this was written, an efficiency too), so it took the wider half and the two pictures were drawn at
    // different scales -- which reads as one sheet being bigger than the other. Fixed layout with a declared 50%
    // makes the split the same every time, whatever either column happens to hold.
    const equalColumns = { tableLayout: 'fixed', width: '100%' };
    const columnWidth = { width: '50%' };

    return <table className="table table-striped table-bordered" style={equalColumns}>
      <tbody>
      <tr>
        {columns.map((column) => (
          <th key={column.key} style={columnWidth}>
            <center> <h4> {column.heading} </h4> </center>
            {machineName(column)}
          </th>
        ))}
      </tr>

      <tr style={{ backgroundColor: 'black', height: svgHeight }}>
        {columns.map((column) => <td key={column.key}> {column.svg} </td>)}
      </tr>

      <tr>
        {columns.map((column) => <td key={column.key}> <center> <h4> {column.description} </h4> </center> </td>)}
      </tr>

      <tr>
        {columns.map((column) => <td key={column.key}> {column.dimensions} </td>)}
      </tr>

      <tr>
        {columns.map((column, position) => <td key={column.key}> {controlsFor(column, position)} </td>)}
      </tr>
      </tbody>
    </table>
  }


  const theme = 'black_bg';
  const [dieCuttingSvg, setDieCuttingSvg] = useState('');
  const [printingSvg, setPrintingSvg] = useState('');

  const svgIdPrefix = () => {
    let prefix = `section_${index}_`;
    if (item.is_item_extra) {
      prefix += item.extra_item_reason;
    }
    return prefix;
  }

  const IdFordieCuttingSvg = () => { return svgIdPrefix() + solution.diecutting_result.uuid + (_multipleColumns ? "_m" : "" ) ;}
  const IdForPrintingSvg =   () => { return svgIdPrefix() + IdFordieCuttingSvg() + [solution.diecutting_result.printing_result.num_x, solution.diecutting_result.printing_result.num_y, solution.diecutting_result.printing_result.l_shape ? "y" : "n"].join('_') }

  const getSvgPostParams = (type, svg_id) => {
    let data = solution;
    data.formula_options = window.dielineOptions;
    data.type = type;
    data.svg_id = svg_id;
    data.drawing_id = window.drawing_id;
    data.id         = window.drawing_id;
    data.layout_dimension_id = solution.material_layout_dimension.id;
    return data;
  }

  const fetchDiecuttingSvg = () => {
    if(solution == undefined) { setTimeout(fetchDiecuttingSvg, 250); return; }

    PostRequest({
      url: '/mcc/layouts-nav-section-combination-svg',
      data: getSvgPostParams('die_cutting', IdFordieCuttingSvg()),
      callback: ( data ) => {
        setDieCuttingSvg( () => data.svg_content )
      }
    })

  }

  const fetchPrintingSvg = () => {
  if(solution == undefined) { setTimeout(fetchDiecuttingSvg, 250); return; }

    PostRequest({
      url: '/mcc/layouts-nav-section-combination-svg',
      data: getSvgPostParams('printing', IdForPrintingSvg()),
      callback: ( data ) => {
        setPrintingSvg( () => data.svg_content )
      }
    })

  }

  const showDiecuttingSvg = () => {
    if(dieCuttingSvg == '') { return "" }

    return <div className={`${frankStyles.blackBgSvg} redraw_theme_${theme}`} dangerouslySetInnerHTML={{__html: dieCuttingSvg}}></div>

  }

  const showPrintingSvg = () => {
    if(printingSvg == '') { return "" }

    return <div className={`${frankStyles.blackBgSvg} redraw_theme_${theme}`} dangerouslySetInnerHTML={{__html: printingSvg}}></div>

  }

  const selectedListView = () => {
    return (<>
      { solutionSvg() }
    </>)
  }

  const onListen = (msg) => {
    if(msg.detail.index == index) {
      handleClear();
    }
  }

  // No pixel height written onto the injected svg here. It used to be given svgHeight, which is the one size
  // the picture must NOT have once #columnPicture's square is capped by a narrow column: a 380px-tall svg
  // inside a 337px square hangs out of the bottom, and the quarter turn swings that overhang sideways -- the
  // same spill, arriving from the inside. frankStyles.blackBgSvg sizes the svg to whatever the square is
  // instead, so the two cannot disagree. (`#layout_svg_<id>` went with it: this fork's markup is the svg on its
  // own -- that wrapper belongs to components/layout/LayoutSvg and components/quote_builder/LayoutSvg, and the
  // selector matched nothing here.)
  const setupSvg = (id) => {
    if($(`#svg_${id}`).length == 0) { return; }

    window.SVGControlsDisabled = true;

    setTimeout( () => {
      if($(`#svg_${id}`).length > 0) {
        performPanZoomInstance(`#svg_${id}`)
      }
    }, 200);
    setTimeout( () => { setupSvg(id) }, 500 )
  }

  // useEffect starts
  useEffect( () => {
    setSolution( () =>  getSolution() )
  }, [item] )

  useEffect(() => {
    setupSvg( IdFordieCuttingSvg() );
  }, [dieCuttingSvg]);

  useEffect(() => {
     setupSvg( IdForPrintingSvg() );
  }, [printingSvg]);

  useEffect(fetchDiecuttingSvg, [item])
  useEffect(fetchPrintingSvg, [item])

  useEffect( () => {
    if(solution == undefined) { return }
    emitEvent("quote_builder.solution_set", { index: index, solution: solution })
  }

  , [solution] )

  // Take this solution's score from the shared cache and then follow it. Only the machine names are wanted
  // from it (see #machineFor), and only /mcc draws those -- the legacy page does not subscribe at all, so it
  // does exactly what it did before this existed.
  //
  // READ FIRST, because a subscriber gets no replay of what is already cached: on the click-through path the
  // solve caches the score before this ever mounts, so a subscribe-only version would wait forever. SUBSCRIBE
  // ANYWAY, even on a hit, because the cache can be CLEARED afterwards -- staying subscribed is what lets the
  // names go away with the score rather than outliving it.
  useEffect(() => {
    if (solution == undefined) { return undefined; }

    const uuid = solution.diecutting_result.uuid;
    if (hasInCache(uuid)) { setScoreData(getFromCache(uuid)); }

    return subscribeToCache((cachedUuid, data) => {
      if (cachedUuid === uuid) { setScoreData(data); }
    });
  }, [solution]);
  // useEffect ends
  MyEventListener([
    {
      key: Constants.layout_dimension_changed,
      callback: onListen
    }
  ])

  return (<>
    {selectedList.length > 0 && solution != undefined && selectedListView() }
  </>)
}

export default FrankSolutionSvgTable;